import { IFormField, IFormSection } from "../web-form.objects";
import { DocumentInfoV2 } from "../../../services/documents/document.objects";
import _ from "lodash";

export class FileUploadInputOptions {
    rootFolderPath: string; // root folder location in S3 (different root for each unique form type)
    propertyIdName: string; // dot notation for finding unique Id of object
    isPdf: boolean = false;

    constructor(rootFolderPath: string, propertyIdName: string, isPdf: boolean = false) {
        this.rootFolderPath = rootFolderPath;
        this.propertyIdName = propertyIdName;
        this.isPdf = isPdf;
    }
}

export class FileUploadInputField implements IFormField {
    fieldId: string; 
    displayName: string; 
    isRequired: boolean;
    propertyName: string; 
    defaultVal: any; 
    forObjProperty: boolean = false;
    shouldTriggerUpdate: boolean = false;

    options: FileUploadInputOptions;

    formSection: IFormSection;

    getFilesRoute: string = "/api/upload-service/get-uploads";
    addFilesRoute: string = "/api/upload-service/add-uploads";
    removeFilesRoute: string = "/api/upload-service/remove-uploads";

    targetItem: any; // form object you are managing file for
    currentDocuments: DocumentInfoV2[];
    filesToUpload: File[];
    filesToDelete: DocumentInfoV2[];

    fileLoadingId: string;
    fileUploadBlockId: string;
    fileUploadInputId: string;
    fileGalleryId: string;
    fileErrorBlock: string;

    constructor(propertyName: string, defaultVal: any, fieldId: string, displayName: string, isRequired: boolean, options: FileUploadInputOptions) {
        this.fieldId = fieldId;
        this.displayName = displayName;
        this.isRequired = isRequired;
        this.propertyName = propertyName;
        this.defaultVal = defaultVal;
        this.options = options;

        this.filesToUpload = [];
        this.targetItem = undefined;

        this.fileLoadingId = `#${this.fieldId}-loading-container`;
        this.fileUploadBlockId = `#${this.fieldId}-file-upload-block`;
        this.fileUploadInputId = `#${this.fieldId}-input`;
        this.fileGalleryId = `#${this.fieldId}-gallery`;
        this.fileErrorBlock = `#${this.fieldId}-error-text`;
    }

    initialize(formSection: IFormSection, targetItem: any) {
        this.formSection = formSection;

        this.filesToUpload = [];
        this.currentDocuments = [];
        this.targetItem = targetItem;

        this.generateHtml();

        this.showLoading(true);
        this.getDocuments().then(result => {
            if (result["items"]) {
                this.currentDocuments = JSON.parse(result["items"]);
                this.initializeFileUpload();
                this.updateDocumentDisplay();
            }
        })
    }

    private initializeFileUpload(): void {
        // disable default behavior for upload blocks
        let uploadBlock = $(this.fileUploadBlockId)

        uploadBlock.on("dragenter dragover dragleave drop", (e) => {
            e.preventDefault();
            e.stopPropagation();
        });

        uploadBlock.on("dragenter dragover", (e) => {
            uploadBlock.addClass("highlight-upload-block");
        })

        uploadBlock.on("dragleave drop", (e) => {
            uploadBlock.removeClass("highlight-upload-block");
        })

        uploadBlock.on("drop", async (e) => {
            this.filesToUpload = [];

            let uploadedFiles = e.originalEvent.dataTransfer.files;

            if (uploadedFiles.length <= 0) {
                return;
            }

            for (let i = 0; i < 1; i++) {
                this.filesToUpload.push(uploadedFiles[i])
            }

            // Validate and Upload
            let isValidUpload = this.validateUpload();
            if (isValidUpload) {
                this.showLoading(true);
                await this.uploadDocuments();
                await this.refreshDocuments();
                this.updateDocumentDisplay();
            }
        })

        let uploadInput = $(this.fileUploadInputId);

        uploadInput.on("change", async (e) => {
            this.filesToUpload = [];

            let uploadedFiles: File[] = uploadInput.prop("files");

            if (uploadedFiles.length <= 0) {
                return;
            }

            for (let i = 0; i < uploadedFiles.length; i++) {
                this.filesToUpload.push(uploadedFiles[i])
            }

            // Validate and Upload
            let isValidUpload = this.validateUpload();
            if (isValidUpload) {
                this.showLoading(true);
                await this.uploadDocuments();
                await this.refreshDocuments();
                this.updateDocumentDisplay();
            }
        });
    }

    private async refreshDocuments() {
        this.filesToUpload = [];
        this.currentDocuments = [];
        let result = await this.getDocuments();
        if (result["items"]) {
            this.currentDocuments = JSON.parse(result["items"]);
        }
    }

    private generateHtml(): void {
        let loadingBlock = `
            <div id="${this.fieldId}-loading-container" class="file-upload-loading">
                <div class="font-size14 mb-1">Loading</div>
                <div class="pl-2">
                    <div class="spinner-border spinner-border-sm" role="status">
                        <span class="sr-only">Loading...</span>
                    </div>
                </div>
            </div>
        `

        let displayFile = "File";
        if (this.options.isPdf) {
            displayFile = "PDF";
        }

        let fileUploadBlock = `
            <div id="${this.fieldId}-file-upload-block" class="file-upload-block d-none">
                <div class="text-center">
                    <i class="fas fa-cloud-upload-alt fa-lg"></i>
                </div>
                <p class="text-center">Drag and Drop ${displayFile} Here!</p>

                <input id="${this.fieldId}-input" class="position-absolute file-upload-input invisible" type="file"/>

                <div class="text-center">
                    <label class="btn btn-small biorep-white-btn choose-file-btn mb-1" for="${this.fieldId}-input">Choose ${displayFile} <i class="fa-solid fa-file pl-1"></i></label>
                </div>
            </div>
        `

        let fileGalleryBlock = `
            <div id="${this.fieldId}-gallery" class="file-upload-gallery d-none">
            </div>
        `

        let fileErrorBlock = `
            <div id="${this.fieldId}-error-text" class="d-none">
                <p class="danger-text">Please upload a file.</p>
            </div>
        `

        let html = `
            ${loadingBlock}
            ${fileUploadBlock}
            ${fileGalleryBlock}
            ${fileErrorBlock}
        `

        $(`#${this.fieldId}`).empty().html(html);
    }

    private showLoading(isLoading: boolean) {
        if (isLoading) {
            $(this.fileUploadBlockId).addClass('d-none');
            $(this.fileGalleryId).addClass('d-none');
            $(this.fileLoadingId).removeClass('d-none');
        }
        else {
            $(this.fileLoadingId).addClass('d-none');
        }
    }

    private async getDocuments() {
        let result: any;

        let currentId = _.get(this.targetItem, this.options.propertyIdName);

        let requestBody = {
            rootFolder: this.options.rootFolderPath,
            propertyName: this.propertyName,
            id: currentId
        }

        try {
            result = await $.ajax(this.getFilesRoute, {
                method: "POST",
                headers: { "RequestVerificationToken": <string>$('input[name="__RequestVerificationToken"]').val() },
                data: JSON.stringify(requestBody),
                contentType: "application/json; charset=utf-8"
            });
            return result;
        }
        catch (error) {
            alert("An error has occurred, please refresh the page and try again.");
            console.log(error);
        }
    }

    private drawFileGallery() {
        $(this.fileGalleryId).empty();

        let content = "";
        // Add Content for Each File
        for (let i = 0; i < this.currentDocuments.length; i++) {
            let doc = this.currentDocuments[i];
            let icon = `<i class="${doc.icon}"></i>`
            content +=
                `<div class="card">
                    <div class="card-header" id="file-heading-${i}">
                        <div class="d-flex justify-content-between align-items-center">
                            <div class="col">
                                <div class="header-text d-flex align-items-center">
                                    ${icon}<span class="expand-text d-block pl-2">${doc.text}</span>
                                </div>
                            </div>
                            <div class="col-auto">
                                <a id="delete-file-btn-${i}" class="text-decoration-none header-delete-btn">
                                    <i class="fa-solid fa-trash-can"></i>
                                </a>
                            </div>
                        </div>
                    </div>
                </div>`
        }

        $(this.fileGalleryId).append(content);
        let fileUploadField = this;

        $(this.fileGalleryId).find(".header-delete-btn").on('click', async function() {
            let fileId: string = $(this).prop("id");
            let index: number = parseInt(fileId.replace("delete-file-btn-", ""));
            let foundFile = fileUploadField.currentDocuments[index];
            if (foundFile) {
                fileUploadField.showLoading(true);
                await fileUploadField.deleteDocument(foundFile);
                await fileUploadField.refreshDocuments();
                fileUploadField.updateDocumentDisplay();
            }
        })
    }

    private updateDocumentDisplay() {
        this.drawFileGallery(); // Updates Gallery

        // If there are documents
        if (this.currentDocuments.length > 0) {
            $(this.fileUploadBlockId).addClass("d-none");
            $(this.fileGalleryId).removeClass("d-none");
        }
        else {
            $(this.fileUploadBlockId).removeClass("d-none");
            $(this.fileGalleryId).addClass("d-none");
        }
        $(this.fileLoadingId).addClass("d-none");

        this.triggerSectionUpdate(); // Anytime documents change
    }

    private async deleteDocument(document: DocumentInfoV2) {
        try {
            let filePath = document.id;
            await $.ajax(this.removeFilesRoute, {
                method: "POST",
                headers: { "RequestVerificationToken": <string>$('input[name="__RequestVerificationToken"]').val(), "FilePath": filePath }
            });
        }
        catch (error) {
            alert("An error has occurred, please refresh the page and try again.");
            console.log(error);
        }
    }

    private async uploadDocuments() {
        let currentId = _.get(this.targetItem, this.options.propertyIdName);

        if (this.filesToUpload.length > 0) {
            let formData: FormData = new FormData();
            for (let i = 0; i < this.filesToUpload.length; i++) {
                let key: string = currentId.toString() + "/" + this.propertyName + "/" + this.filesToUpload[i].name;
                formData.append(key, this.filesToUpload[i]);
            }

            let result: any;
            try {
                result = await $.ajax(this.addFilesRoute, {
                    method: "POST",
                    headers: { "RequestVerificationToken": <string>$('input[name="__RequestVerificationToken"]').val(), "RootFolderPath": this.options.rootFolderPath, "PropertyName": this.propertyName, "Id": currentId.toString() },
                    contentType: false,
                    cache: false,
                    processData: false,
                    data: formData
                });
            }
            catch (error) {
                alert("An error has occurred, please refresh the page and try again.");
                console.log(error);
            }
        }
    }

    getValue(): any {
        return;
    }

    setValue(formData: any) {
        return;
    }

    private setErrorText(errorText: string = null) {
        if (errorText == null) {
            $(this.fileErrorBlock).text('');
            $(this.fileErrorBlock).addClass('d-none');
        }
        else {
            $(this.fileErrorBlock).text(errorText);
            $(this.fileErrorBlock).removeClass('d-none');
        }
    }

    private validateUpload(): boolean {
        if (this.options.isPdf) {
            if (this.filesToUpload.some(file => !file.type.includes("pdf"))) {
                this.setErrorText("Please select a PDF document.")
                return false;
            }
        }

        this.setErrorText();
        return true;
    }

    checkValid(): boolean {
        if (this.isRequired) {
            // empty or spaces
            if (this.currentDocuments.length == 0) {
                return false;
            }
            return true;
        }
        else {
            return true;
        }
    }

    checkChanged(): boolean {
        if (this.currentDocuments.length == 0) {
            return false;
        }
        return true;
    }

    displayValid(isValid: boolean) {
        if (isValid) {
            $(`#${this.fieldId}`).addClass("is-valid").removeClass("is-invalid");
        }
        else {
            $(`#${this.fieldId}`).removeClass("is-valid").addClass("is-invalid");
        }
    }

    resetValidDisplay() {
        $(`#${this.fieldId}`).removeClass("is-valid").removeClass("is-invalid");
    }

    triggerSectionUpdate() {
        if (this.formSection && this.shouldTriggerUpdate) {
            this.formSection.updateSection();
        }
    }
}

